/* Norrviva — PDP runtime
 * Three light interactions, no framework:
 *   1. Gallery thumbnail → main image swap (data-pdp-gallery-*).
 *   2. Quantity stepper (data-pdp-qty-*) — also updates the Add-to-cart label.
 *   3. Inline roll calculator (data-pdp-rollcalc) — suggests a quantity,
 *      never auto-applies it. The "Set qty to N →" button must be clicked.
 *
 * Accordion rows use native <details>/<summary>; this script only enforces
 * "one open at a time" within an accordion group, matching the design.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  /* ----------------------------------------------------------------
   * Gallery
   * ---------------------------------------------------------------- */
  function initGallery(root) {
    var main = root.querySelector('[data-pdp-gallery-main]');
    var thumbs = root.querySelectorAll('[data-pdp-gallery-thumb]');
    var shots = root.querySelectorAll('[data-pdp-gallery-shot]');
    if (!main || !shots.length) return;

    function setActive(idx) {
      var key = String(idx);
      thumbs.forEach(function (t) {
        var active = t.getAttribute('data-pdp-gallery-thumb') === key;
        t.setAttribute('data-active', active ? 'true' : 'false');
        if (active) {
          t.setAttribute('aria-current', 'true');
        } else {
          t.removeAttribute('aria-current');
        }
      });
      shots.forEach(function (s) {
        s.setAttribute('data-active', s.getAttribute('data-pdp-gallery-shot') === key ? 'true' : 'false');
      });
    }

    // Thumbnail click → snap the track to that shot (swipe does the same by hand).
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = parseInt(thumb.getAttribute('data-pdp-gallery-thumb'), 10) || 0;
        main.scrollTo({ left: idx * main.clientWidth, behavior: 'smooth' });
        setActive(idx);
      });
    });

    // Keep the active thumbnail in sync as the shot is swiped/scrolled.
    var raf = null;
    main.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        if (!main.clientWidth) return;
        var idx = Math.round(main.scrollLeft / main.clientWidth);
        idx = Math.max(0, Math.min(shots.length - 1, idx));
        setActive(idx);
      });
    }, { passive: true });
  }

  /* ----------------------------------------------------------------
   * Quantity stepper
   * ---------------------------------------------------------------- */
  function getQty(input) {
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) return 1;
    return v;
  }

  function setQty(input, value) {
    var v = Math.max(1, parseInt(value, 10) || 1);
    if (input.value !== String(v)) {
      input.value = String(v);
      // Native event so any listener (e.g. our label updater) reacts.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function initQty(scope) {
    var qty = scope.querySelector('[data-pdp-qty]');
    if (!qty) return null;
    var input = qty.querySelector('[data-pdp-qty-input]');
    var dec = qty.querySelector('[data-pdp-qty-dec]');
    var inc = qty.querySelector('[data-pdp-qty-inc]');
    if (!input) return null;

    if (dec) dec.addEventListener('click', function () { setQty(input, getQty(input) - 1); });
    if (inc) inc.addEventListener('click', function () { setQty(input, getQty(input) + 1); });
    input.addEventListener('blur', function () { setQty(input, getQty(input)); });

    return input;
  }

  /* ----------------------------------------------------------------
   * Dynamic Add-to-cart label — "Add {qty} roll | rolls · {total}"
   * Updates live as the qty changes. Total formatted via the shop's
   * money format (Shopify.formatMoney exists from vendor.min.js).
   * ---------------------------------------------------------------- */
  function formatMoney(amountCents) {
    var fmt = (window.theme && (window.theme.money_product_price_format || window.theme.money_format)) || '{{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amountCents, fmt);
    }
    // Minimal fallback: amountCents is in shop minor units (öre / cents).
    return (amountCents / 100).toFixed(2);
  }

  function initAddLabel(scope, qtyInput) {
    var buy = scope.querySelector('[data-pdp-buy]');
    var label = scope.querySelector('[data-pdp-add-label]');
    if (!buy || !label || !qtyInput) return;

    var unitPrice = parseInt(buy.getAttribute('data-unit-price'), 10);
    var singular = buy.getAttribute('data-singular') || 'roll';
    var plural = buy.getAttribute('data-plural') || 'rolls';
    var template = buy.getAttribute('data-add-template') || 'Add [qty] [unit] · [total]';
    if (!isFinite(unitPrice) || unitPrice <= 0) return;

    function render() {
      var qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      var total = formatMoney(unitPrice * qty);
      var unit = qty === 1 ? singular : plural;
      label.textContent = template
        .replace('[qty]', String(qty))
        .replace('[unit]', unit)
        .replace('[total]', total);
    }

    qtyInput.addEventListener('change', render);
    qtyInput.addEventListener('input', render);
    render();
  }

  /* ----------------------------------------------------------------
   * Trust line — stock vs the SELECTED quantity (Jeremie 18 Jul).
   * Enough stock for the chosen qty -> in-stock line, otherwise the
   * new-batch line. The section only emits data-stock for tracked
   * variants, so untracked products keep their server-rendered line.
   * ---------------------------------------------------------------- */
  function initTrustLine(scope, qtyInput) {
    var trust = scope.querySelector('[data-pdp-trust]');
    if (!trust || !qtyInput || !trust.hasAttribute('data-stock')) return;

    var stock = parseInt(trust.getAttribute('data-stock'), 10);
    var lineStock = trust.getAttribute('data-line-stock') || '';
    var lineBatch = trust.getAttribute('data-line-batch') || '';
    if (!isFinite(stock) || !lineStock || !lineBatch) return;

    function render() {
      var qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      var next = stock >= qty ? lineStock : lineBatch;
      if (trust.textContent !== next) trust.textContent = next;
    }

    qtyInput.addEventListener('change', render);
    qtyInput.addEventListener('input', render);
    render();
  }

  /* ----------------------------------------------------------------
   * Roll calculator — suggest only, never auto-apply
   * ---------------------------------------------------------------- */
  function computeRolls(opts) {
    var w = parseFloat(opts.wallW);    // metres
    var h = parseFloat(opts.wallH);    // metres
    if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;

    var rollLength = parseFloat(opts.rollLengthCm);     // 1005
    var rollWidth = parseFloat(opts.rollWidthCm);       // 53
    var repeat = parseFloat(opts.repeatCm);             // e.g. 64
    var buffer = parseFloat(opts.buffer);               // e.g. 1.1
    if (!isFinite(repeat) || repeat <= 0) repeat = 64;
    if (!isFinite(buffer) || buffer <= 0) buffer = 1.1;

    var hCm = h * 100;
    var wCm = w * 100;

    // Match the legacy formula (assets equivalent of the old custom_liquid).
    var stripLen = hCm + repeat;
    if (stripLen <= 0 || rollLength < stripLen) return null;

    var usableLengthPerRoll = Math.floor(rollLength / stripLen) * stripLen;
    var areaPerRoll = (usableLengthPerRoll * rollWidth) / 10000;       // m²
    var totalArea = (hCm * wCm) / 10000;                               // m²
    if (areaPerRoll <= 0) return null;

    return Math.ceil((totalArea / areaPerRoll) * buffer);
  }

  function initRollCalc(scope, qtyInput) {
    var rc = scope.querySelector('[data-pdp-rollcalc]');
    if (!rc) return;

    var wInput = rc.querySelector('[data-pdp-rollcalc-w]');
    var hInput = rc.querySelector('[data-pdp-rollcalc-h]');
    var resultEl = rc.querySelector('[data-pdp-rollcalc-result]');
    if (!wInput || !hInput || !resultEl) return;

    var data = rc.dataset;
    var helperLabel = data.helperLabel || 'Enter your wall dimensions to see a suggested quantity.';
    var singular = data.resultSingular || 'roll';
    var plural = data.resultPlural || 'rolls';
    var applyLabel = data.applyLabel || 'Set quantity to';

    function render() {
      var rolls = computeRolls({
        wallW: wInput.value,
        wallH: hInput.value,
        rollLengthCm: data.rollLengthCm,
        rollWidthCm: data.rollWidthCm,
        repeatCm: data.patternRepeatCm,
        buffer: data.patternBuffer
      });

      if (rolls == null) {
        resultEl.innerHTML = '<span class="caption nvv-pdp__rc-helper">' + helperLabel + '</span>';
        return;
      }

      var unit = rolls === 1 ? singular : plural;
      var currentQty = qtyInput ? parseInt(qtyInput.value, 10) || 0 : 0;
      var html =
        '<div class="nvv-pdp__rc-main">' +
          '<span class="serif nvv-pdp__rc-num">' + rolls + '</span>' +
          '<span class="serif italic nvv-pdp__rc-unit">' + unit + '</span>' +
        '</div>';
      if (qtyInput && rolls !== currentQty) {
        // Arrow points UP because the qty stepper this button updates
        // sits on the row above the calculator panel.
        html +=
          '<button type="button" class="nvv-pdp__rc-apply" data-pdp-rollcalc-apply>' +
            applyLabel + ' ' + rolls + ' ↑' +
          '</button>';
      }
      resultEl.innerHTML = html;

      var applyBtn = resultEl.querySelector('[data-pdp-rollcalc-apply]');
      if (applyBtn && qtyInput) {
        applyBtn.addEventListener('click', function () {
          setQty(qtyInput, rolls);
          render();
        });
      }
    }

    wInput.addEventListener('input', render);
    hInput.addEventListener('input', render);
    if (qtyInput) qtyInput.addEventListener('change', render);

    // Don't run the math until the panel opens — keeps first paint light.
    rc.addEventListener('toggle', function () {
      if (rc.open) render();
    });
  }

  /* ----------------------------------------------------------------
   * Accordion — one open at a time per group
   * ---------------------------------------------------------------- */
  function initAccordion(group) {
    var rows = group.querySelectorAll('details.nvv-pdp__acc-row');
    rows.forEach(function (row) {
      row.addEventListener('toggle', function () {
        if (!row.open) return;
        rows.forEach(function (other) {
          if (other !== row && other.open) other.open = false;
        });
      });
    });
  }

  /* ----------------------------------------------------------------
   * Boot
   * ---------------------------------------------------------------- */
  ready(function () {
    document.querySelectorAll('.nvv-pdp').forEach(function (root) {
      var gallery = root.querySelector('[data-pdp-gallery]');
      if (gallery) initGallery(gallery);

      var qtyInput = initQty(root);
      initAddLabel(root, qtyInput);
      initTrustLine(root, qtyInput);
      initRollCalc(root, qtyInput);

      root.querySelectorAll('[data-pdp-accordion]').forEach(initAccordion);
    });
  });
})();
